/**
 * promptTemplateDiff.ts — side-by-side comparison of two skill config
 * versions (story E0-BO-CFG-SKILL-REGISTRY-ADMIN, "Compare").
 *
 * A dependency-free diff: the fields this panel lets an operator edit
 * (grounding[*], product_facts, classifiers[*].system_prompt) plus the
 * read-only agent-loop config (agent_config_json's script.steps[*] and
 * boundaries.rules[*], ADR-082) are flattened to comparable strings and
 * compared path-by-path. No diff library is added — a simple side-by-side
 * of the changed fields is enough for this surface, and `graph_config_json`
 * (never editable, superseded by the agent loop) is deliberately excluded.
 */

import type { Script, SkillVersionDetail } from '../../api/types';
import { diffScripts } from './scriptDiff';

/** Flatten the editable regions of a version's prompt_template_json to path → string. */
const flattenComparable = (detail: SkillVersionDetail): Record<string, string> => {
  const out: Record<string, string> = {};

  const grounding = detail.prompt_template_json.grounding ?? {};
  Object.entries(grounding).forEach(([intent, entry]) => {
    out[`grounding.${intent}.instruction`] = entry.instruction;
    out[`grounding.${intent}.anchor_template`] = entry.anchor_template;
    out[`grounding.${intent}.reference_facts`] = JSON.stringify(entry.reference_facts);
    out[`grounding.${intent}.mandatory_string_categories`] =
      entry.mandatory_string_categories.join(', ');
    // fact_categories / mandatory_fact_patterns live HERE, per grounding
    // entry — not on product_facts (see api/types.ts GroundingEntry).
    // Pre-migration-0075 rows genuinely lack these keys (see api/types.ts).
    // Diff-compare as `{}` — display-only, never fed back into a save.
    out[`grounding.${intent}.fact_categories`] = JSON.stringify(entry.fact_categories ?? {});
    out[`grounding.${intent}.mandatory_fact_patterns`] = JSON.stringify(
      entry.mandatory_fact_patterns ?? {},
    );
  });

  const productFacts = detail.prompt_template_json.product_facts ?? {};
  Object.entries(productFacts).forEach(([key, value]) => {
    out[`product_facts.${key}`] = value;
  });

  const classifiers = detail.prompt_template_json.classifiers ?? {};
  Object.entries(classifiers).forEach(([key, config]) => {
    out[`classifiers.${key}.system_prompt`] = config.system_prompt;
  });

  // agent_config_json (ADR-082 agent loop) — read-only compare, no editor.
  const agentConfig = detail.agent_config_json;
  const script = agentConfig?.script as { steps?: unknown } | undefined;
  const steps = Array.isArray(script?.steps) ? (script.steps as Record<string, unknown>[]) : [];
  steps.forEach((step) => {
    const stepId = step.step_id;
    if (typeof stepId === 'string') {
      if (typeof step.goal === 'string')
        out[`agent_config.script.steps.${stepId}.goal`] = step.goal;
      if (typeof step.exit_when === 'string') {
        out[`agent_config.script.steps.${stepId}.exit_when`] = step.exit_when;
      }
      if ('advance_when' in step) {
        out[`agent_config.script.steps.${stepId}.advance_when`] = JSON.stringify(step.advance_when);
      }
      if ('allowed_tools' in step) {
        out[`agent_config.script.steps.${stepId}.allowed_tools`] = JSON.stringify(
          step.allowed_tools,
        );
      }
      if ('required_facts' in step) {
        out[`agent_config.script.steps.${stepId}.required_facts`] = JSON.stringify(
          step.required_facts,
        );
      }
    }
  });

  const boundaries = agentConfig?.boundaries as { rules?: unknown } | undefined;
  const rules = Array.isArray(boundaries?.rules) ? (boundaries.rules as unknown[]) : [];
  rules.forEach((rule, index) => {
    out[`agent_config.boundaries[${index}]`] = JSON.stringify(rule);
  });

  // ADR-084 AgentConfigDoc shape (S3a): definition.persona, cadence_instructions,
  // facts[] and the plural scripts[] — additive to the ADR-082 `script`/legacy
  // handling above, never overlapping it (different keys).
  const definition = agentConfig?.definition as { persona?: Record<string, unknown> } | undefined;
  const persona = definition?.persona;
  if (persona != null) {
    if (typeof persona.name === 'string')
      out['agent_config.definition.persona.name'] = persona.name;
    if (typeof persona.voice_summary === 'string') {
      out['agent_config.definition.persona.voice_summary'] = persona.voice_summary;
    }
    if (typeof persona.disclosure_rule === 'string') {
      out['agent_config.definition.persona.disclosure_rule'] = persona.disclosure_rule;
    }
  }

  if (typeof agentConfig?.cadence_instructions === 'string') {
    out['agent_config.cadence_instructions'] = agentConfig.cadence_instructions;
  }

  const facts = Array.isArray(agentConfig?.facts)
    ? (agentConfig.facts as Record<string, unknown>[])
    : [];
  facts.forEach((fact) => {
    if (typeof fact.key === 'string') out[`agent_config.facts.${fact.key}`] = JSON.stringify(fact);
  });

  // ADR-085 D1/D6 — eligibility/document_checklist, keyed on the fixed
  // question key / checklist key (never array index), same discipline as
  // facts[] above.
  const eligibility = agentConfig?.eligibility as
    | {
        age_limit_years?: unknown;
        weight_limit_tonnes?: unknown;
        reask_attempts?: unknown;
        questions?: Record<string, unknown>;
      }
    | null
    | undefined;
  if (eligibility != null) {
    if (eligibility.age_limit_years != null)
      out['agent_config.eligibility.age_limit_years'] = String(eligibility.age_limit_years);
    if (eligibility.weight_limit_tonnes != null) {
      out['agent_config.eligibility.weight_limit_tonnes'] = String(eligibility.weight_limit_tonnes);
    }
    if (eligibility.reask_attempts != null) {
      out['agent_config.eligibility.reask_attempts'] = String(eligibility.reask_attempts);
    }
    Object.entries(eligibility.questions ?? {}).forEach(([key, question]) => {
      out[`agent_config.eligibility.questions.${key}`] = JSON.stringify(question);
    });
  }

  const documentChecklist = agentConfig?.document_checklist as
    | {
        sequence?: unknown[];
        types?: Record<string, unknown>;
        request_templates?: Record<string, unknown>;
      }
    | null
    | undefined;
  if (documentChecklist != null) {
    (documentChecklist.sequence ?? [])
      .filter((k): k is string => typeof k === 'string')
      .forEach((key) => {
        out[`agent_config.document_checklist.${key}.type`] = JSON.stringify(
          documentChecklist.types?.[key],
        );
        out[`agent_config.document_checklist.${key}.request_template`] = JSON.stringify(
          documentChecklist.request_templates?.[key],
        );
      });
  }

  // scripts[] is diffed separately, keyed on product_script_key/step_id — see
  // diffScripts below, called from diffSkillVersions. Not flattened here.

  return out;
};

export interface VersionDiffRow {
  path: string;
  /** null = this path is absent in the base version. */
  before: string | null;
  /** null = this path is absent in the compare version. */
  after: string | null;
}

/** Path-sorted rows for every path whose value differs (or is added/removed) between the two versions. */
export const diffSkillVersions = (
  base: SkillVersionDetail,
  compare: SkillVersionDetail,
): VersionDiffRow[] => {
  const baseFlat = flattenComparable(base);
  const compareFlat = flattenComparable(compare);
  const paths = new Set([...Object.keys(baseFlat), ...Object.keys(compareFlat)]);

  const rows: VersionDiffRow[] = [];
  [...paths].sort().forEach((path) => {
    const before = baseFlat[path] ?? null;
    const after = compareFlat[path] ?? null;
    if (before !== after) rows.push({ path, before, after });
  });
  const scriptsOf = (detail: SkillVersionDetail): Script[] | undefined =>
    Array.isArray(detail.agent_config_json?.scripts)
      ? (detail.agent_config_json.scripts as Script[])
      : undefined;
  rows.push(...diffScripts(scriptsOf(base), scriptsOf(compare)));
  return rows;
};
